import type { CourseAggregate, Session } from '@/features/modules/types';
import type { ProfilesResponse } from '@/features/user/types/get-profiles';

import {
  ANZ_COUNTRIES,
  BATCHES_150_PLUS_PRODUCT_ID,
  ELIGIBLE_COURSE_CODE_SUFFIX,
  IMO_ENROLLMENT_GRADES,
  IMO_PRODUCT_ID,
  MATHLETE_VARIANT_IDS,
  NAPLAN_VARIANT_IDS,
  SCHOOL_CURRICULUM_VARIANT_IDS,
} from '../utils/constants';

/**
 * Checks if the user is in Math Wizard course (course code ends with '150')
 */
export const isMathWizard = (courseAggregate?: CourseAggregate): boolean => {
  if (!courseAggregate?.course_code) return false;
  return courseAggregate.course_code.endsWith(ELIGIBLE_COURSE_CODE_SUFFIX);
};

/**
 * Checks if the user is NOT enrolled in school curriculum
 * Returns true if user has NOT enrolled in any course with school curriculum variant IDs
 */
export const isNotEnrolledToSchoolCurriculum = (
  enrolledCourses?: ProfilesResponse.EnrolledCourse[]
): boolean => {
  if (!enrolledCourses || enrolledCourses.length === 0) return true;

  return !enrolledCourses.some(course =>
    SCHOOL_CURRICULUM_VARIANT_IDS.includes(course.variant_id)
  );
};

/**
 * Checks if the user should be shown the school curriculum enrollment notification
 * Based on:
 * 1. User is in Math Wizard course (course_code ends with '150')
 * 2. User is NOT enrolled in school curriculum courses
 */
export const shouldShowSchoolCurriculumEnrollment = (
  courseAggregate?: CourseAggregate,
  enrolledCourses?: ProfilesResponse.EnrolledCourse[]
): boolean => {
  const isEligibleCourse = isMathWizard(courseAggregate);
  const notEnrolled = isNotEnrolledToSchoolCurriculum(enrolledCourses);

  return isEligibleCourse && notEnrolled;
};

/**
 * Checks if the user is NOT enrolled in NAPLAN
 * Returns true if user has NOT enrolled in any course with NAPLAN variant IDs
 */
export const isNotEnrolledToNaplan = (
  enrolledCourses?: ProfilesResponse.EnrolledCourse[]
): boolean => {
  if (!enrolledCourses || enrolledCourses.length === 0) return true;

  return !enrolledCourses.some(course =>
    NAPLAN_VARIANT_IDS.includes(course.variant_id)
  );
};

/**
 * Checks if user is from ANZ region (Australia or New Zealand)
 */
export const isFromANZRegion = (country?: string): boolean => {
  if (!country) return false;
  return ANZ_COUNTRIES.includes(country);
};

/**
 * Checks if user has completed at least 30 sessions
 * Counts sessions that have video URLs
 */
export const hasCompleted30Sessions = (allSessions?: Session[]): boolean => {
  if (!allSessions || allSessions.length === 0) return false;

  // Count sessions that have video URLs
  const sessionsWithVideos = allSessions.filter(session => session?.video?.url);

  return (sessionsWithVideos?.length ?? 0) > 30;
};

/**
 * Checks if the user should be shown the NAPLAN enrollment notification
 * Based on the logic from Dashboard.tsx:
 * 1. User is in Math Wizard course (course_code ends with '150')
 * 2. User is NOT enrolled in NAPLAN
 * 3. User is from ANZ region (AU or NZ)
 * 4. User has completed 30+ sessions with video URLs
 * OR
 * User has a NAPLAN course enrolled but without a batch_id
 */
export const shouldShowNaplanEnrollment = (
  courseAggregate?: CourseAggregate,
  enrolledCourses?: ProfilesResponse.EnrolledCourse[],
  country?: string,
  allSessions?: Session[]
): boolean => {
  const isMathWizardCourse = isMathWizard(courseAggregate);
  const notEnrolled = isNotEnrolledToNaplan(enrolledCourses);
  const fromANZ = isFromANZRegion(country);
  const completedSessions = hasCompleted30Sessions(allSessions);

  // Check if user has NAPLAN course enrolled but without batch_id
  const naplanCourse = enrolledCourses?.find(course =>
    NAPLAN_VARIANT_IDS.includes(course.variant_id)
  );
  const hasNaplanWithoutBatch = naplanCourse && !naplanCourse.batch_id;

  return (
    (isMathWizardCourse && notEnrolled && fromANZ && completedSessions) ||
    !!hasNaplanWithoutBatch
  );
};

/**
 * Checks if the user is NOT enrolled in IMO
 * Returns true if user has NOT enrolled in any course with IMO product ID
 */
export const isNotEnrolledToIMO = (
  enrolledCourses?: ProfilesResponse.EnrolledCourse[]
): boolean => {
  if (!enrolledCourses || enrolledCourses.length === 0) return true;

  return !enrolledCourses.some(course => course.product_id === IMO_PRODUCT_ID);
};

/**
 * Checks if the user is in the premium Math Wizard course (BATCHES_150_PLUS_PRODUCT_ID)
 */
export const isInPremiumMathWizard = (
  enrolledCourses?: ProfilesResponse.EnrolledCourse[],
  selectedVariantId?: string
): boolean => {
  if (!enrolledCourses || !selectedVariantId) return false;

  const selectedCourse = enrolledCourses.find(
    course => course.variant_id === selectedVariantId
  );

  return selectedCourse?.product_id === BATCHES_150_PLUS_PRODUCT_ID;
};

/**
 * Checks if the user's grade is eligible for IMO enrollment
 */
export const isEligibleGradeForIMO = (
  courseAggregate?: CourseAggregate
): boolean => {
  if (!courseAggregate?.grade) return false;

  return (IMO_ENROLLMENT_GRADES as readonly string[]).includes(
    courseAggregate.grade
  );
};

/**
 * Checks if the user should be shown the IMO enrollment notification
 * Based on the logic from starry-night Dashboard.tsx:
 * 1. User is NOT enrolled in IMO course
 * 2. User's selected product is BATCHES_150_PLUS_PRODUCT_ID (premium Math Wizard)
 * 3. User's grade is in IMO_ENROLLMENT_GRADES (['6-7', '8-8'])
 */
export const shouldShowIMOEnrollment = (
  courseAggregate?: CourseAggregate,
  enrolledCourses?: ProfilesResponse.EnrolledCourse[],
  selectedVariantId?: string
): boolean => {
  const notEnrolled = isNotEnrolledToIMO(enrolledCourses);
  const isPremiumUser = isInPremiumMathWizard(
    enrolledCourses,
    selectedVariantId
  );
  const eligibleGrade = isEligibleGradeForIMO(courseAggregate);

  return notEnrolled && isPremiumUser && eligibleGrade;
};

/**
 * Checks if the user should be shown the Mathlete 101 batch registration notification
 * Based on the logic from starry-night Dashboard.tsx and useIsRegistrationRequired hook:
 * 1. User is enrolled in a Mathlete course (has a variant_id matching MATHLETE_VARIANT_IDS)
 * 2. User does NOT have a batch_id assigned (needs to register for a batch)
 *
 * This is different from other enrollments - the user is already enrolled but needs
 * to complete batch registration to start the course.
 */
export const shouldShowMathleteRegistration = (
  enrolledCourses?: ProfilesResponse.EnrolledCourse[]
): boolean => {
  if (!enrolledCourses || enrolledCourses.length === 0) return false;

  // Check if there's any Mathlete course without a batch_id
  return enrolledCourses.some(
    course =>
      MATHLETE_VARIANT_IDS.includes(course.variant_id) && !course.batch_id
  );
};
